// Schedule and Playoffs (SCHEDULE.md): the season as a list of periods with
// my matchup on each row and every matchup a tap away, and the bracket,
// projected from the standings until the playoffs start, live after. Drawn
// through the kit (LOOKS.md Part 7).

import { useMemo, useState } from 'react';
import type {
  BracketGame,
  BracketRound,
  BracketView,
  MatchupSummary,
  ScheduleView,
  TeamRef,
} from '@fsp/domain';
import { racesOnPoints, valueOf } from '@fsp/domain';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useBracket, useLeagueBase, useReaderZone, useSchedule } from '../data/hooks';
import { useAtLeast } from '../data/detail';
import { LiveDot, TeamMark } from '../kit';
import { Menu } from '../kit/overlays';
import { Icon } from '../kit/Icon';
import { Panel } from '../kit/Panel';
import { Button, Cover, Segments, Text, Title } from '../kit/ui';
import '../styles/screens/league-screens.css';
import '../styles/screens/schedule.css';
import { useTier } from '../shell/useTier';
import { ordinal } from './shared/RosterMoveSheet';
import { useActiveLeague } from '../data/LeaguesProvider';
import { FromLeague } from './shared/FromLeague';
import { TeamName } from './shared/Names';
import { Term } from '../kit/Why';
import { GameGrid } from './scoreboard/GameGrid';
import { SeasonWeeks } from './schedule/SeasonWeeks';
import { UpcomingSeason } from './schedule/UpcomingSeason';
import { drawSchedule } from '../data/moves';
import { messageOf } from '../data/api';
import { RoadToPlayoffs, SeasonSoFar, StrengthOfSchedule } from './schedule/SeasonSide';
import { recordText, teamsIn, weekDates, weekNow, weeksOf } from './schedule/season';

type Tab = 'schedule' | 'playoffs';

/**
 * The screen is split in two for E12, and every league screen that moves to
 * the API needs the same split.
 *
 * The body derives a dozen values from the view before it renders anything, so
 * an early return for "not here yet" would have to sit after every hook in the
 * file — one added above it later is a rules-of-hooks violation that React
 * reports as a confusing render error somewhere else. Handing the resolved
 * view to a child makes the impossible state unrepresentable instead: the body
 * cannot run without one.
 */
export function ScheduleScreen() {
  return (
    <FromLeague snapshot={useSchedule()}>{(view) => <ScheduleBody schedule={view} />}</FromLeague>
  );
}

function ScheduleBody({ schedule }: { schedule: ScheduleView }) {
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'playoffs' ? 'playoffs' : 'schedule';
  // ONE CHANGE TO THE ADDRESS, THE REST KEPT: the tab and the team are both
  // in it, so a link to another team's season opens on it.
  const patch = (next: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(next)) {
          if (v === null) n.delete(k);
          else n.set(k, v);
        }
        return n;
      },
      { replace: true },
    );
  const setTab = (t: Tab) => patch({ tab: t === 'playoffs' ? 'playoffs' : null });
  const tier = useTier();
  const phone = tier === 'phone';
  const [grid, setGrid] = useState(false);
  const allPlay = useActiveLeague()?.league.format.id === 'all-play';
  // WHOSE SEASON (B-386): mine, or the team the address names. A league
  // played against the field serves my week alone, so it has only mine.
  const teams = teamsIn(schedule);
  const field = schedule.periods.some((p) => p.myField !== null);
  const asked = params.get('team');
  const teamId =
    !field && asked !== null && teams.some((t) => t.id === asked)
      ? asked
      : schedule.myTeamId || (teams[0]?.id ?? '');
  const team = teams.find((t) => t.id === teamId) ?? null;
  const mine = teamId === schedule.myTeamId;
  const pick = (id: string) => patch({ team: id === schedule.myTeamId ? null : id });

  const picker =
    tab === 'schedule' && !field && team !== null && teams.length > 1 ? (
      <Menu
        title="Whose season"
        maxHeight={420}
        entries={[
          { kind: 'label', label: 'Whose season' },
          ...teams.map((t) => ({
            kind: 'item' as const,
            key: t.id,
            label: t.isMine ? `${t.name} (you)` : t.name,
            lead: <TeamMark team={t} size={18} />,
            current: t.id === teamId,
            onClick: () => pick(t.id),
          })),
        ]}
        target={
          // A native button: the menu hands its target a ref, which the
          // kit's Button does not take.
          // Named by its words, with what it picks before them unseen.
          <button type="button" className="fsp-sc-picker">
            <TeamMark team={team} size={18} />
            <span className="fsp-sr">Whose season: </span>
            <span className="fsp-sc-picker-name">{team.name}</span>
            <Icon name="expand" size="sm" />
          </button>
        }
      />
    ) : null;

  const tabs = (
    <span className="fsp-sched-tabs">
      <Segments
        label="Schedule or playoffs"
        items={[
          { id: 'schedule', label: 'Schedule' },
          { id: 'playoffs', label: 'Playoffs' },
        ]}
        value={tab}
        onChange={setTab}
      />
    </span>
  );
  // ON A PHONE'S SEASON THE SWITCH STAYS AT THE TOP while the weeks scroll
  // under it (B-486), as every other phone screen's segments do; it was a
  // scroll back up away from the season's last week.
  const stickTabs = phone && tab === 'schedule';

  const header = (
    <div className="fsp-waivers-head">
      <div className="fsp-sched-head-body">
        <Title>{tab === 'schedule' ? 'Schedule' : 'Playoffs'}</Title>
        <div className="fsp-sc-headline">
          {picker}
          <span>
            {/* A race on points has no record (B-361), and a league with no
                bracket has nobody to make it (B-362). Another team's place
                is the standings' (not on the schedule), so its record alone. */}
            {mine || tab === 'playoffs' ? (
              <>
                {racesOnPoints(schedule.league.format.id)
                  ? ''
                  : `${recordText(schedule.record)} · `}
                {schedule.record.rank === null ? '—' : ordinal(schedule.record.rank)} of{' '}
                {schedule.record.of} ·{' '}
              </>
            ) : team !== null ? (
              `${recordText(team.record)} · `
            ) : null}
            {schedule.playoffTeamCount === 0
              ? 'No playoffs'
              : `${schedule.playoffTeamCount} make the playoffs`}
          </span>
        </div>
      </div>
      {stickTabs ? null : tabs}
      {tab === 'schedule' && !phone && schedule.upcoming === null ? (
        <Button
          size="xs"
          variant="quiet"
          icon={grid ? 'list' : 'leagues'}
          onClick={() => setGrid((g) => !g)}
        >
          {grid ? 'List' : 'Grid'}
        </Button>
      ) : null}
    </div>
  );

  return (
    // On a phone the season scrolls as one screen (UX-38); the bracket
    // keeps its own box, which scrolls sideways when it must.
    <div className={`fsp-screen${tab === 'schedule' ? ' fsp-flow' : ''}`}>
      <div className="fsp-pinned">{header}</div>
      {stickTabs ? <div className="fsp-pinned fsp-pinned-stick">{tabs}</div> : null}
      {tab === 'schedule' && schedule.upcoming !== null ? (
        <UpcomingSeason
          upcoming={schedule.upcoming}
          myTeamId={schedule.myTeamId}
          onDraw={async () => {
            const reply = await drawSchedule(schedule.league.id);
            return reply.status === 'ok'
              ? { ok: true }
              : {
                  ok: false,
                  message: messageOf(
                    reply,
                    'The schedule was not drawn again.',
                    'Could not reach the site. Nothing was drawn.',
                  ),
                };
          }}
        />
      ) : tab === 'schedule' ? (
        grid && !phone ? (
          <ScheduleGrid schedule={schedule} teamId={teamId} />
        ) : team === null ? (
          <div className="fsp-empty">No season to show yet.</div>
        ) : (
          <Season
            schedule={schedule}
            team={team}
            allPlay={allPlay}
            onPick={pick}
            onPicture={() => setTab('playoffs')}
          />
        )
      ) : (
        <Bracket />
      )}
    </div>
  );
}

// ── Schedule: the week under way, the season, and its summary ─────────────

/**
 * THE SEASON AT A GLANCE (B-386): the week under way (or the next) as the
 * scoreboard's tiles, the team's game first; the season a row a week; and
 * beside them on a desktop, under them on a phone, the season so far, the
 * road to the playoffs and, at Everything, the strength of schedule.
 */
function Season({
  schedule,
  team,
  allPlay,
  onPick,
  onPicture,
}: {
  schedule: ScheduleView;
  team: TeamRef;
  allPlay: boolean;
  onPick: (teamId: string) => void;
  onPicture: () => void;
}) {
  const tier = useTier();
  const base = useLeagueBase();
  const zone = useReaderZone();
  const bracket = valueOf(useBracket());
  const now = useMemo(() => Date.parse(schedule.updatedAt), [schedule.updatedAt]);
  const week = weekNow(schedule, now);
  // A PHONE SHOWS THE TEAM'S OWN GAME and a door to the rest: four games
  // stacked put the season a long scroll below them. A week the team sits
  // out shows them all.
  const own = week?.matchups.filter(
    (m) => m.home.team.id === team.id || m.away.team.id === team.id,
  );
  const shown = week === null ? [] : tier === 'phone' && own!.length > 0 ? own! : week.matchups;
  const weeks = schedule.periods.flatMap((p) => weeksOf(p, team.id));
  const mine = team.id === schedule.myTeamId;
  return (
    <div className={`fsp-duo fsp-duo-${tier}`}>
      <div className="fsp-duo-main">
        {week !== null ? (
          <Panel
            title={week.period.isCurrent ? 'This week' : 'Next up'}
            meta={
              <span className="fsp-sc-week-meta">
                <span className="fsp-muted fsp-sb-count">
                  {week.period.label} · {weekDates(week.period.start, week.period.end, zone)}
                </span>
                <Link
                  to={`${base}/scoreboard?period=${week.period.index}`}
                  className="fsp-linklike fsp-sb-more"
                >
                  {shown.length < week.matchups.length
                    ? `All ${week.matchups.length} games`
                    : 'Scoreboard'}
                </Link>
              </span>
            }
          >
            <GameGrid games={shown} stats={week.stats} first={team.id} />
          </Panel>
        ) : null}
        <SeasonWeeks view={schedule} teamId={team.id} allPlay={allPlay} />
      </div>
      <div className="fsp-duo-side">
        <SeasonSoFar
          team={team}
          weeks={weeks}
          rank={mine ? schedule.record.rank : null}
          of={schedule.record.of}
        />
        <RoadToPlayoffs view={schedule} team={team} bracket={bracket} onPicture={onPicture} />
        <StrengthOfSchedule view={schedule} teamId={team.id} onPick={onPick} />
      </div>
    </div>
  );
}

function resultLabel(
  m: MatchupSummary,
  myTeamId: string,
): { text: string; tone: 'win' | 'loss' | 'live' | 'pre' | 'tie' } {
  const mine = m.home.team.id === myTeamId ? m.home : m.away;
  const theirs = mine === m.home ? m.away : m.home;
  // A week to come is a game to come: the grid names the opponent and the
  // week's rows say the chance where one is served (B-386).
  if (m.status === 'pre') return { text: 'Upcoming', tone: 'pre' };
  const score = `${mine.score.toFixed(1)}–${theirs.score.toFixed(1)}`;
  if (m.status === 'live') return { text: score, tone: 'live' };
  // WHO WON, as the result says (N1): a tie the league broke on bench points
  // is a W or an L at the same score, never a T.
  if (m.winnerTeamId === mine.team.id) return { text: `W ${score}`, tone: 'win' };
  if (m.winnerTeamId === theirs.team.id) return { text: `L ${score}`, tone: 'loss' };
  return { text: `T ${score}`, tone: 'tie' };
}

// ── Schedule: the grid, teams down and periods across ─────────────────────

function ScheduleGrid({ schedule: view, teamId }: { schedule: ScheduleView; teamId: string }) {
  const navigate = useNavigate();
  const base = useLeagueBase();
  const teams = useMemo(() => {
    const seen = new Map<string, MatchupSummary['home']['team']>();
    for (const p of view.periods)
      for (const m of p.matchups) {
        seen.set(m.home.team.id, m.home.team);
        seen.set(m.away.team.id, m.away.team);
      }
    return [...seen.values()].sort(
      (a, b) => Number(b.isMine) - Number(a.isMine) || a.name.localeCompare(b.name),
    );
  }, [view]);
  const regular = view.periods.filter((p) => p.kind === 'regular');
  // HOW HARD EACH TEAM'S REST OF THE SEASON IS, at Everything (B-386): the
  // rank the schedule serves, 1 the hardest, in a column after the weeks.
  const pro = useAtLeast('pro');
  const strength = new Map(view.strength.map((s) => [s.teamId, s]));
  const left = pro && view.strength.some((s) => s.remainingRank !== null);
  return (
    <div className="fsp-regions fsp-board-one">
      <section className="fsp-region fsp-sched-scroll" aria-label="Season grid">
        <table className="fsp-sched-grid">
          <thead>
            <tr>
              <th />
              {regular.map((p) => (
                <th key={p.period.index} className={p.period.isCurrent ? 'current' : ''}>
                  {p.period.index}
                  {p.rivalry ? (
                    <>
                      {' '}
                      <Term
                        tip="Rivalry week: every team with a rival plays it."
                        name="Rivalry week"
                      >
                        ★
                      </Term>
                    </>
                  ) : null}
                </th>
              ))}
              {left ? (
                <th>
                  <Term tip="How hard the rest of the regular season is, 1 the hardest: the opponents’ records now, then their points a week. Strength of schedule has the numbers.">
                    Left
                  </Term>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {teams.map((t) => (
              // The row of the team whose season is picked, mine by default.
              <tr key={t.id} className={t.id === teamId ? 'mine' : ''}>
                <th scope="row">
                  <span className="fsp-sched-opp">
                    <TeamMark team={t} size={18} />
                    {/* The team's page from its row of the grid (B-220). */}
                    <TeamName team={t}>{t.abbreviation}</TeamName>
                  </span>
                </th>
                {regular.map((p) => {
                  // THE TEAM'S GAMES THAT WEEK: one, or two (Q6, B-614),
                  // the first first.
                  const games = p.matchups
                    .filter((x) => x.home.team.id === t.id || x.away.team.id === t.id)
                    .sort((a, b) => Number(a.secondGame) - Number(b.secondGame));
                  if (games.length === 0)
                    return (
                      <td key={p.period.index} className="bye">
                        —
                      </td>
                    );
                  const cell = (m: (typeof games)[number], className: string) => {
                    const res = resultLabel(m, t.id);
                    const opp = m.home.team.id === t.id ? m.away.team : m.home.team;
                    // THE RESULT IN A LETTER as well as a color (UX-28), and
                    // the whole game when asked: a hover, a long press or a
                    // focus. A tap opens the matchup.
                    const mark =
                      res.tone === 'win'
                        ? 'W'
                        : res.tone === 'loss'
                          ? 'L'
                          : res.tone === 'tie'
                            ? 'T'
                            : null;
                    return (
                      <Term
                        key={m.id}
                        className={`fsp-sched-cell ${className}`}
                        tip={`${p.period.label}${m.secondGame ? ', second game' : ''}: ${m.home.team.name} vs ${m.away.team.name}${res.tone === 'pre' ? '' : `, ${res.text}`}${p.rivalry ? ', rivalry week' : ''}.`}
                        name={`${p.period.label}${m.secondGame ? ', second game' : ''}, ${t.abbreviation} against ${opp.abbreviation}`}
                        onActivate={() => navigate(`${base}/matchup/${m.id}`)}
                      >
                        {mark ? (
                          <span className="fsp-sched-mark" aria-hidden>
                            {mark}
                          </span>
                        ) : null}
                        {opp.abbreviation}
                      </Term>
                    );
                  };
                  if (games.length > 1)
                    // A WEEK OF TWO: each game its own door and its own color.
                    return (
                      <td
                        key={p.period.index}
                        className={`fsp-sched-two ${p.rivalry ? 'rivalry' : ''}`}
                      >
                        {games.map((m) => cell(m, `fsp-sched-${resultLabel(m, t.id).tone}`))}
                      </td>
                    );
                  const m = games[0]!;
                  return (
                    <td
                      key={p.period.index}
                      className={`fsp-sched-${resultLabel(m, t.id).tone} ${p.rivalry ? 'rivalry' : ''}`}
                    >
                      {cell(m, '')}
                    </td>
                  );
                })}
                {left ? (
                  <td className="fsp-num fsp-sched-left">
                    {strength.get(t.id)?.remainingRank ?? '—'}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

// ── Playoffs: the bracket ─────────────────────────────────────────────────

function Bracket() {
  // A real league's bracket is its own, read from its table and rounds
  // (PLAN-2026-09-26-season-end S4); the simulator's is the tour's alone.
  return <FromLeague snapshot={useBracket()}>{(view) => <BracketBody view={view} />}</FromLeague>;
}

function BracketBody({ view }: { view: BracketView }) {
  const navigate = useNavigate();
  const base = useLeagueBase();
  const tier = useTier();
  const phone = tier === 'phone';
  const [which, setWhich] = useState<'playoff' | 'consolation'>('playoff');
  const rounds = which === 'consolation' && view.consolation ? view.consolation : view.rounds;
  const open = (g: BracketGame) =>
    g.matchupId ? navigate(`${base}/matchup/${g.matchupId}`) : undefined;

  const bar = (
    <div className="fsp-bracket-bar">
      <div className="fsp-sched-head-body">
        <div className="fsp-bracket-headline">
          {view.status === 'live' ? <LiveDot /> : null}
          <Text weight="bold">{view.headline}</Text>
        </div>
        <div className="fsp-sub fsp-ui-wrap-text">
          {/* Before a week is played there are no seeds to be through (B-193). */}
          {view.status === 'projected' && view.throughPeriod > 0
            ? `Through Week ${view.throughPeriod}. `
            : ''}
          {view.teamCount} make it{view.byes ? `; the top ${view.byes} rest in round one` : ''}.
          {view.consolationKind === 'toilet-bowl' ? ' Toilet Bowl: lose and you keep playing.' : ''}
        </div>
      </div>
      {view.consolation ? (
        <span className="fsp-sched-tabs fsp-sched-tabs-wide">
          <Segments
            label="Which bracket"
            items={[
              { id: 'playoff', label: 'Playoffs' },
              {
                id: 'consolation',
                label: view.consolationKind === 'toilet-bowl' ? 'Toilet Bowl' : 'Consolation',
              },
            ]}
            value={which}
            onChange={setWhich}
          />
        </span>
      ) : null}
      <Button size="xs" variant="quiet" onClick={() => navigate(`${base}/standings`)}>
        Standings
      </Button>
    </div>
  );

  return (
    <>
      <div className="fsp-pinned">{bar}</div>
      <div className="fsp-regions fsp-board-one">
        <section className="fsp-region fsp-region-plain fsp-sched-scroll" aria-label="Bracket">
          {rounds.length === 0 ? (
            <div className="fsp-empty">No playoffs in this league. The season decides it.</div>
          ) : (
            <div
              className={`fsp-bracket ${phone ? 'stacked fsp-bracket-cols-1' : `fsp-bracket-cols-${Math.min(rounds.length, 6)}`}`}
            >
              {rounds.map((r) => (
                <BracketColumn key={r.name} round={r} onOpen={open} />
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
}

/** One round of the bracket: its games, each opening its matchup. The front page's too (B-408). */
export function BracketColumn({
  round,
  onOpen,
}: {
  round: BracketRound;
  onOpen: (g: BracketGame) => void;
}) {
  return (
    <div className="fsp-bracket-round">
      <div className="fsp-bracket-round-head">
        <span className="fsp-cap">{round.name}</span>
        <span className="fsp-sub">{round.period.label}</span>
      </div>
      <div className="fsp-bracket-games">
        {round.games.map((g) => (
          <div
            key={g.id}
            className={`fsp-surface fsp-bracket-game ${g.matchupId ? 'fsp-row-clickable fsp-cover-host' : ''} status-${g.status}`}
          >
            {/* The game opens its matchup; each team's name opens the team,
                above that door (B-220). */}
            {g.matchupId ? <Cover label="Open the matchup" onClick={() => onOpen(g)} /> : null}
            {g.status === 'bye' ? (
              <BracketSide slot={g.top} score={null} winner={false} note="Rests this round" />
            ) : (
              <>
                <BracketSide
                  slot={g.top}
                  score={g.topScore}
                  winner={g.winnerTeamId !== null && g.winnerTeamId === g.top?.team.id}
                />
                <BracketSide
                  slot={g.bottom}
                  score={g.bottomScore}
                  winner={g.winnerTeamId !== null && g.winnerTeamId === g.bottom?.team.id}
                />
                {g.status === 'live' ? (
                  <div className="fsp-bracket-live">
                    <LiveDot label="Live" />
                  </div>
                ) : null}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function BracketSide({
  slot,
  score,
  winner,
  note,
}: {
  slot: BracketGame['top'];
  score: number | null;
  winner: boolean;
  note?: string;
}) {
  const standard = useAtLeast('standard');
  if (!slot) return <div className="fsp-bracket-side tbd">To be decided</div>;
  const tbd = slot.seed === 0;
  return (
    <div
      className={`fsp-bracket-side ${winner ? 'winner' : ''} ${tbd ? 'tbd' : ''} ${slot.team.isMine ? 'mine' : ''}`}
    >
      <span className="fsp-bracket-seed fsp-num">{tbd ? '' : slot.seed}</span>
      {tbd ? (
        <span className="fsp-muted fsp-grow">{slot.source}</span>
      ) : (
        <>
          <TeamMark team={slot.team} size={20} />
          <span className="fsp-bracket-who">
            <span className="fsp-bracket-name">
              <TeamName team={slot.team} />
            </span>
            {/* Each seed's record, from Standard (B-386): what put it there. */}
            {standard ? (
              <span className="fsp-sub fsp-num fsp-bracket-record">
                {recordText(slot.team.record)}
              </span>
            ) : null}
          </span>
        </>
      )}
      {note ? <span className="fsp-sub">{note}</span> : null}
      {score !== null ? (
        <span className="fsp-num fsp-bracket-score">{score.toFixed(1)}</span>
      ) : null}
    </div>
  );
}
